/** @prettier */

import React from 'react';
import _get from 'lodash/get';
import usePageViews from './hooks/usePageViews';

/**
 * @description This is a wrapper component for saving page views
 * to local storage to trigger certain events based off the amount
 * of times a user has viewed the page
 *
 * @param {JSX.Element} WrappedComponent
 * @param {string} pageName
 * @param {string} propToTriggerUpdate This is a prop that we can use to monitor a
 * certain prop in the case that the user changes pages via a React Router <Link/>
 * which may not cause the page component to 'remount' which means the page views
 * won't be incremented. Param should be passed as a string to use with lodash get
 *
 * example: `'match.history.params.propName'`
 * @returns
 */
const withPageViews = (WrappedComponent, pageName, propToTriggerUpdate = null) => {
  const WrappedWithPageViews = props => {
    const updateTrigger = _get(props, propToTriggerUpdate);
    const pageViews = usePageViews(pageName, updateTrigger);
    return <WrappedComponent pageViews={pageViews} {...props} />;
  };

  /**
   * If the Wrapped Component is a top level route and has a `fetch` property
   * we need to assign it here so SSR can pick it up
   */
  if (WrappedComponent.fetch) {
    WrappedWithPageViews.fetch = WrappedComponent.fetch;
  }

  return WrappedWithPageViews;
};

export default withPageViews;
